import React, { useEffect, useState } from "react";
import AnnouncementOutlinedIcon from "@mui/icons-material/AnnouncementOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useNavigate, useParams } from "react-router-dom";
import {
  AppBar,
  Body,
  ErrorCard,
  LoadingCard,
  LoadingPage,
  SuccessCard,
} from "../components";
import {
  useDownloadReceiptMutation,
  useQueryGroupTheme,
  useQueryPaymentStatus,
  useQueryServiceByServiceId,
  useQueryTheme,
} from "../api/queries";
import { ThemeProps } from "../models/theme";
import { getCorporateType, getStorageToken } from "../utils";
import { useDimensions, useTheme } from "../hooks";
import { Alert, AlertTitle, Box, Button } from "@mui/material";
import { TRANSACTION_STATUS } from "../constants";
import { t } from "i18next";
import { getContrastTextColor } from "../utils/colorManager";
import { useGlobalContext } from "../context/globalContext";

const Transaction = () => {
  const token = getStorageToken();
  const { isSmall } = useDimensions();
  const { corporateName = "", id = "", transactionId = "" } = useParams();
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();
  const paddingX = isSmall ? 2 : "31%";
  const { pending, success, errored } = TRANSACTION_STATUS;
  const [status, setStatus] = useState<string>(pending);
  const downloadMutation = useDownloadReceiptMutation({
    authorisation: token!,
    itemId: transactionId,
  });

  const {
    data,
    isLoading: isLoadingTheme,
    isFetching,
  } = useQueryTheme({
    corporateName,
    enabled: !!token && !!corporateName,
    authorisation: token || "",
  });
  const { totalAmount } = useGlobalContext();

  const { updateTheme, theme } = useTheme();

  useEffect(() => {
    updateTheme(data as ThemeProps);
  }, [data]);

  const { data: corporate, isLoading: isLoadingCorporateThemeDetails } =
    useQueryGroupTheme({
      id: theme?.id,
      groupType: getCorporateType(theme?.url_type),
      enable: !!theme?.id,
      authorisation: token || "",
    });

  const corporateId = corporate?.id || "";

  const service = useQueryServiceByServiceId({
    corporateId,
    serviceId: id,
    enabled: !!token && !!id,
    authorisation: token || "",
  });

  const {
    data: paymentData,
    isLoading: isLoadingPayment,
    error: paymentError,
  } = useQueryPaymentStatus({
    paymentItemId: transactionId,
    enabled: !!token && !!transactionId,
    authorisation: token || "",
  });

  useEffect(() => {
    // si erreur réseau / API
    if (paymentError) {
      setStatus(errored);
      setError((paymentError as any)?.message || "Payment status error");
      return;
    }

    if (!paymentData) return;

    const rawStatus = (paymentData?.status || paymentData?.data?.status || "")
      .toString()
      .toUpperCase();

    if (rawStatus === "SUCCESS" || rawStatus === "PAID") {
      setStatus(success);
      setError("");
    } else if (
      rawStatus === "FAILED" ||
      rawStatus === "ERROR" ||
      rawStatus === "DECLINED"
    ) {
      setStatus(errored);
      setError(
        paymentData?.message || paymentData?.data?.message || "Payment failed"
      );
    } else {
      setStatus(pending);
      setError("");
    }
  }, [paymentData, paymentError, success, errored, pending]);

  if (
    isFetching ||
    isLoadingTheme ||
    isLoadingCorporateThemeDetails ||
    service?.isLoading ||
    isLoadingPayment
  )
    return <LoadingPage />;

  const openReportLink = () => {
    const url =
      "https://maviance.atlassian.net/servicedesk/customer/portal/4/group/5/create/63";
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const isFinalResut = status === success || status === errored;

  return (
    <Body>
      <AppBar theme={theme} />
      {error && (
        <Box ml={1} mb={3}>
          <Alert
            severity={"error"}
            variant={"standard"}
            onClose={() => setError("")}
          >
            <AlertTitle sx={{ fontWeight: "bold" }}>{"Error"}</AlertTitle>
            {error}
          </Alert>
        </Box>
      )}
      <Box>
        <Box
          sx={{
            display: "flex",
            height: "100%",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* {isFetching && <ProcessingCard />} */}
          {status === pending && <LoadingCard totalAmount={totalAmount} />}
          {status === success && (
            <SuccessCard
              totalAmount={totalAmount}
              accountNumber={transactionId}
              // text={pending}
            />
          )}
          {status === errored && (
            <ErrorCard
              totalAmount={totalAmount}
              accountNumber={transactionId}
              errorMessage={""}
            />
          )}
        </Box>
        {isFinalResut && (
          <Box
            sx={{
              alignItems: "center",
              paddingX,
            }}
          >
            <Button
              variant="contained"
              type="submit"
              fullWidth
              size="large"
              sx={{
                marginBottom: 2,
                bgcolor: theme.color_primary,
                color: getContrastTextColor(theme.color_primary),
              }}
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                navigate(`/${corporateName}`);
              }}
            >
              {t("stepper_4.make_other_payment")}
            </Button>

            <Button
              variant="outlined"
              type="submit"
              fullWidth
              size="large"
              sx={{
                marginBottom: 2,
                borderColor: theme.color_secondary,
                color: getContrastTextColor(theme.color_secondary),
              }}
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={() => {
                downloadMutation.mutate();
              }}
            >
              {t("button.download_reciept")}
            </Button>

            <Button
              variant="outlined"
              type="submit"
              fullWidth
              size="large"
              startIcon={<AnnouncementOutlinedIcon />}
              color="secondary"
              onClick={openReportLink}
            >
              {t("button.report")}
            </Button>
          </Box>
        )}
      </Box>
    </Body>
  );
};

export default Transaction;
